import { useEffect, useRef, type JSX } from 'react';
import { Link } from 'react-router';
import { ArrowRight } from 'lucide-react';
import { getAllPosts } from '../blog/manifest.js';
import { PostCard } from '../blog/post-card.js';
import { formatDate } from '../../lib/format-price.js';
import { usePreferencesStore } from '../../stores/preferences.js';
import type { BlogMeta } from '../blog/types.js';

// Hand-picked favorites to show off the blog on the landing page. Looked up via
// getAllPosts (draft-tolerant) so the carousel renders even while these posts
// are still draft (the prod index/sitemap publish flip is the later SEO pass).
const FAVORITE_SLUGS = [
  'ai-product-photography-no-studio',
  'ai-marketing-creatives-at-scale',
  'agentbrush-prompting-guide',
  '2d-game-asset-generation',
] as const;

const ROUNDED_SQUARE =
  'M16 0 H48 A16 16 0 0 1 64 16 V48 A16 16 0 0 1 48 64 H16 A16 16 0 0 1 0 48 V16 A16 16 0 0 1 16 0 Z';

// White rounded square with a transparent arrow knocked out — the play-button
// style from the blog spotlight (single evenodd path).
function ArrowControl({
  dir,
  onClick,
}: {
  dir: 'prev' | 'next';
  onClick: () => void;
}): JSX.Element {
  const arrow = dir === 'next' ? 'M26 21 L43 32 L26 43 Z' : 'M38 21 L21 32 L38 43 Z';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dir === 'next' ? 'Next articles' : 'Previous articles'}
      className="group/arrow pointer-events-auto rounded-2xl focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
    >
      <svg
        width="44"
        height="44"
        viewBox="0 0 64 64"
        aria-hidden="true"
        className="drop-shadow-[0_4px_14px_rgba(0,0,0,0.4)] transition-transform duration-300 group-hover/arrow:scale-105"
      >
        <path fill="#ffffff" fillRule="evenodd" d={`${ROUNDED_SQUARE} ${arrow}`} />
      </svg>
    </button>
  );
}

export function BlogShowcaseSection(): JSX.Element | null {
  const locale = usePreferencesStore((s) => s.locale);
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const nudgeRef = useRef(0); // pending px to advance from an arrow click

  const bySlug = new Map(getAllPosts().map((p) => [p.slug, p] as const));
  const posts = FAVORITE_SLUGS.map((slug) => bySlug.get(slug)).filter(
    (p): p is BlogMeta => p !== undefined,
  );
  const count = posts.length;

  // Pan with translate3d (sub-pixel, GPU) rather than scrollLeft — scrollLeft
  // quantizes to whole pixels, so the sub-pixel-per-frame auto-advance gets
  // rounded away and never moves. `pos` is a float accumulator. Three identical
  // copies + a per-frame wrap keep it in the middle copy, so it loops forever
  // in both directions; arrows feed the same accumulator.
  useEffect(() => {
    if (count === 0) return;
    const view = viewportRef.current;
    const track = trackRef.current;
    if (!view || !track) return;

    const oneCopy = (): number => track.scrollWidth / 3;
    let pos = oneCopy(); // start in the middle copy
    track.style.transform = `translate3d(${-pos}px, 0, 0)`;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let raf = 0;
    let last = 0;
    const AUTO = 55; // auto-pan, px per second
    const tick = (now: number): void => {
      const tr = trackRef.current;
      const vw = viewportRef.current;
      if (tr && vw) {
        if (last === 0) last = now;
        const dt = Math.min((now - last) / 1000, 0.05); // cap so a backgrounded tab doesn't jump
        last = now;
        const w = tr.scrollWidth / 3;
        if (w > 0) {
          // Auto-advance unless the pointer is directly over the cards.
          if (!reduce && !vw.matches(':hover')) pos += AUTO * dt;
          // Ease in any pending arrow nudge.
          if (Math.abs(nudgeRef.current) > 0.5) {
            const move = nudgeRef.current * Math.min(1, dt * 8);
            pos += move;
            nudgeRef.current -= move;
          } else {
            nudgeRef.current = 0;
          }
          // Wrap within the middle copy (invisible — the copies are identical).
          if (pos >= 2 * w) pos -= w;
          else if (pos < w) pos += w;
          tr.style.transform = `translate3d(${-pos}px, 0, 0)`;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [count]);

  if (count === 0) return null;

  const nudge = (dir: 'prev' | 'next'): void => {
    const vw = viewportRef.current;
    nudgeRef.current += (dir === 'next' ? 1 : -1) * (vw?.clientWidth ?? 600) * 0.6;
  };

  const copy = (prefix: string, hidden: boolean): JSX.Element => (
    <div className="flex shrink-0" aria-hidden={hidden || undefined}>
      {posts.map((post) => (
        <div key={`${prefix}-${post.slug}`} className="mr-6 w-[300px] shrink-0 compact:w-[320px]">
          <PostCard post={post} dateLabel={formatDate(post.date, locale)} />
        </div>
      ))}
    </div>
  );

  return (
    <section id="blog" aria-labelledby="blog-showcase-heading" className="w-full py-12">
      <div className="mx-auto flex max-w-[1280px] flex-col items-center gap-3 px-6 text-center">
        <span className="font-mono text-caption uppercase tracking-[0.2em] text-muted">
          From the blog
        </span>
        <h2
          id="blog-showcase-heading"
          className="text-[clamp(2rem,4vw,3rem)] font-semibold leading-[1.1] tracking-[-0.02em] text-primary"
        >
          Guides and use cases
        </h2>
        <p className="max-w-xl text-body text-secondary">
          Real projects built from your agent, plus tips to get more out of every generation.
        </p>
      </div>

      <div className="relative mt-8">
        <div ref={viewportRef} className="agb-carousel-viewport w-full">
          <div ref={trackRef} className="flex w-max will-change-transform">
            {copy('a', false)}
            {copy('b', true)}
            {copy('c', true)}
          </div>
        </div>
        {/* Arrows aligned to the page content margins (max-w-[1280px] + px-6),
            vertically centered on the card image. */}
        <div className="pointer-events-none absolute inset-x-0 top-[40%] hidden -translate-y-1/2 standard:block">
          <div className="mx-auto flex max-w-[1280px] items-center justify-between px-6">
            <ArrowControl dir="prev" onClick={() => nudge('prev')} />
            <ArrowControl dir="next" onClick={() => nudge('next')} />
          </div>
        </div>
      </div>

      <div className="mt-8 flex justify-center px-6">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-small font-semibold text-primary transition-colors hover:text-accent-cyan focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          Browse all articles
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
}
